import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Console read model for the Incident reports that name a Duty Call (stack step 4).
 *
 * Reads `management_field_incident_list`, which now projects `duty_call_id` and gates on
 * `has_account_permission(account_id, 'evidence.view')` (or platform access). Do not point these
 * reads at `field_incidents`: that table's RLS is role membership, so a revoked `evidence.view`
 * would still return officer-typed titles through a Call.
 *
 * `DutyCallsDesk.canViewEvidence` is display honesty only. Without the permission, omit the
 * Incidents section and skip the count read. Do not render `No incidents on this Call.` for a
 * permission miss. SQL is the real gate.
 *
 * What is never selected, at any call site in this file: `notes`, `client_event_id`,
 * `reported_by_user_id`, `metadata`, and anything from `evidence_attachments`. There are no
 * storage keys and no signed URLs on this path. `incident_id` is read for one purpose only, the
 * Open in Incidents href, and never reaches visible chrome.
 */

export const DUTY_CALL_INCIDENT_TABLE = "management_field_incident_list";
export const DUTY_CALL_INCIDENT_SELECT =
  "incident_id,title,incident_status,category,severity,occurred_at,submitted_at";
/** One Call's receipts. Far above the field model (a couple of subjects at one scene). */
export const DUTY_CALL_INCIDENT_LIMIT = 50;
/** Guards the count read when a workspace shows the full open + recent Call window. */
export const DUTY_CALL_INCIDENT_COUNT_LIMIT = 500;

export interface DutyCallIncidentPage {
  receipts: DutyCallIncidentReceipt[];
  /** True when the Call has more Incidents than this page read. */
  truncated: boolean;
}

export interface DutyCallIncidentReceipt {
  /** Href only. Must not reach visible chrome. */
  incidentId: string;
  title: string;
  status: string;
  category: string | null;
  severity: string | null;
  occurredAt: string | null;
  submittedAt: string | null;
}

function nonemptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function recordRows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (row): row is Record<string, unknown> => typeof row === "object" && row !== null,
      )
    : [];
}

export function mapDutyCallIncidentReceipt(
  raw: Record<string, unknown>,
): DutyCallIncidentReceipt | null {
  const incidentId = nonemptyString(raw.incident_id);
  const title = nonemptyString(raw.title);
  const status = nonemptyString(raw.incident_status);
  if (incidentId === null || title === null || status === null) return null;
  return {
    incidentId,
    title,
    status,
    category: nonemptyString(raw.category),
    severity: nonemptyString(raw.severity),
    occurredAt: nonemptyString(raw.occurred_at),
    submittedAt: nonemptyString(raw.submitted_at),
  };
}

/**
 * Receipts for one selected Call, newest occurrence first.
 *
 * Reads one row past the cap so a capped list can say it is capped. Silently returning the first
 * N would present a partial list as the whole list, which is the same failure the unreadable-row
 * throw below exists to prevent.
 */
export async function fetchDutyCallIncidents(
  client: SupabaseClient,
  accountId: string,
  callId: string,
  limit: number = DUTY_CALL_INCIDENT_LIMIT,
): Promise<DutyCallIncidentPage> {
  const { data, error } = await client
    .from(DUTY_CALL_INCIDENT_TABLE)
    .select(DUTY_CALL_INCIDENT_SELECT)
    .eq("account_id", accountId)
    .eq("duty_call_id", callId)
    .order("occurred_at", { ascending: false })
    .limit(limit + 1);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load the Incidents on this Call.");
  }
  const rows = recordRows(data);
  if (rows.length !== data.length) {
    throw new Error("Could not load the Incidents on this Call.");
  }
  const receipts: DutyCallIncidentReceipt[] = [];
  for (const row of rows) {
    const receipt = mapDutyCallIncidentReceipt(row);
    // A receipt names a durable report. Dropping an unreadable one would show a shorter list as
    // if it were the whole list, which is the one thing this section must not do.
    if (receipt === null) throw new Error("Could not load the Incidents on this Call.");
    receipts.push(receipt);
  }
  return { receipts: receipts.slice(0, limit), truncated: receipts.length > limit };
}

/**
 * How many Incidents name each of the given Calls. Selects the link column and nothing else, so
 * the count on a list row cannot become a second, quieter copy of the receipt data.
 */
export async function fetchDutyCallIncidentCounts(
  client: SupabaseClient,
  accountId: string,
  callIds: readonly string[],
): Promise<ReadonlyMap<string, number> | null> {
  if (callIds.length === 0) return new Map();
  const { data, error } = await client
    .from(DUTY_CALL_INCIDENT_TABLE)
    .select("duty_call_id")
    .eq("account_id", accountId)
    .in("duty_call_id", [...callIds])
    .limit(DUTY_CALL_INCIDENT_COUNT_LIMIT);
  if (error !== null || !Array.isArray(data)) {
    throw new Error("Could not load Incident counts for these Calls.");
  }
  // Truncation here is not a short list, it is wrong arithmetic: an unordered cap would let a row
  // claim "2 incidents" over a detail listing three. Report nothing rather than a wrong number.
  if (data.length >= DUTY_CALL_INCIDENT_COUNT_LIMIT) return null;
  const counts = new Map<string, number>();
  for (const row of recordRows(data)) {
    const callId = nonemptyString(row.duty_call_id);
    if (callId === null) continue;
    counts.set(callId, (counts.get(callId) ?? 0) + 1);
  }
  return counts;
}

/**
 * The human Call reference for one Incident, for the Incidents desk chip.
 *
 * The list view now carries `duty_call_id`. This still does not project `call_ref` onto every
 * Incident row: one selected record needs one Call lookup. Returns null for a standalone report,
 * and also when the link exists but the Call is unreadable, so the chip can only ever claim a
 * reference it read.
 */
export async function fetchIncidentCallRef(
  client: SupabaseClient,
  accountId: string,
  incidentId: string,
): Promise<string | null> {
  const link = await client
    .from(DUTY_CALL_INCIDENT_TABLE)
    .select("duty_call_id")
    .eq("account_id", accountId)
    .eq("incident_id", incidentId)
    .limit(1);
  if (link.error !== null || !Array.isArray(link.data)) {
    throw new Error("Could not load the Call reference for this Incident.");
  }
  const callId = nonemptyString(recordRows(link.data)[0]?.duty_call_id);
  if (callId === null) return null;

  const call = await client
    .from("duty_calls")
    .select("call_ref")
    .eq("account_id", accountId)
    .eq("id", callId)
    .limit(1);
  if (call.error !== null || !Array.isArray(call.data)) {
    throw new Error("Could not load the Call reference for this Incident.");
  }
  return nonemptyString(recordRows(call.data)[0]?.call_ref);
}
